import { Button, ButtonLevel, ButtonSize, TextSize } from '@vizit/atoms';
import { Trash01Icon } from '@vizit/icons';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import * as React from 'react';

interface DeleteProductDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * The id of the product to delete.
   */
  productId: string | null;
  /**
   * Callback when the product is deleted.
   */
  onDelete: (id: string) => Promise<void>;
}

function DeleteProductDialog(props: DeleteProductDialogProps) {
  const { isOpen = false, onOpenChange, onDelete, productId, ...other } = props;

  const handleOnDelete = React.useCallback(async () => {
    if (productId) {
      await onDelete?.(productId);
    }
  }, [onDelete, productId]);

  const handleClose = React.useCallback(() => {
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      close();
    }
  }, [onOpenChange]);

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content className="w-[400px] max-[320px]:w-full" dialogClassName="gap-4 max-w-[400px]">
          <Dialog.Header variant={DialogHeaderVariant.Icon} status={DialogHeaderStatus.Error} icon={Trash01Icon}>
            <Dialog.Title showEllipsis={true} className="text-md">
              Delete product page?
            </Dialog.Title>
          </Dialog.Header>
          <div className="px-12">
            <Dialog.Subtitle elementType="p" size={TextSize.Md} className="text-secondary" showEllipsis={false}>
              Are you sure you want to permanently delete this product page? This action will remove all product page
              data, including assets and generated images.
            </Dialog.Subtitle>
          </div>
          <Dialog.Footer>
            <Button className="w-full" size={ButtonSize.Lg} level={ButtonLevel.Secondary} onPress={handleClose}>
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              type="submit"
              level={ButtonLevel.Danger}
              onPress={handleOnDelete}
            >
              Delete page
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { DeleteProductDialog };
export type { DeleteProductDialogProps };
